import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useSchoolBootstrap } from "@/hooks/use-school-bootstrap";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { logoutSession } from "@/services/api/auth";
import { apiFetch } from "@/services/http/api-fetch";
import { downloadApiFile } from "@/services/http/download";
import { useAuthStore } from "@/stores/authStore";

type PrivacySettingsProps = {
  tenantSlug: string;
  userEmail: string;
};

const ERROR_OPTIONS = { namespace: "privacy.errors" } as const;

export function PrivacySettings({ tenantSlug, userEmail }: PrivacySettingsProps) {
  const { tenant: schoolTenant } = useSchoolBootstrap(tenantSlug);
  // MT-1 S21 (FR-51): export and deletion act on this school's account only; the copy says so.
  const school = schoolTenant?.name ?? tenantSlug;
  const t = useTranslations("privacy");
  const tRoot = useTranslations();
  const navigate = useNavigate();
  const clearSession = useAuthStore((state) => state.clearSession);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [loadingExport, setLoadingExport] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  // LRN-15: clear the session only once the cancellation page has replaced this one. Router
  // navigations are transitions while the auth store re-renders at once, so clearing first let
  // the signed-out guards (TenantSlugGuard, the page's own) send the user to /login instead.
  const signOutOnLeave = useRef(false);
  // W-27: the confirmation replaces the focused submit button, so focus moves into it (its safe
  // "No" button) when it opens and back to the submit button when it is dismissed.
  const confirmNoButton = useRef<HTMLButtonElement>(null);
  const deleteSubmitButton = useRef<HTMLButtonElement>(null);
  const wasConfirming = useRef(false);
  useEffect(() => {
    if (confirming) confirmNoButton.current?.focus();
    // Only when focus was lost with the confirmation (not while the user types the email).
    else if (wasConfirming.current && document.activeElement === document.body) {
      deleteSubmitButton.current?.focus();
    }
    wasConfirming.current = confirming;
  }, [confirming]);
  useEffect(
    () => () => {
      if (signOutOnLeave.current) clearSession();
    },
    [clearSession],
  );

  async function handleExport(format: "json" | "csv") {
    setLoadingExport(true);
    setExportMessage(null);
    setExportError(null);
    try {
      // LRN-24: the shared helper keeps the blob URL alive long enough for Safari to save it.
      await downloadApiFile(`/v1/privacy/export?format=${format}`, `lms-data-export.${format}`);
      setExportMessage(t("exportDownloaded", { format: format.toUpperCase() }));
    } catch (err) {
      setExportError(apiErrorText(err, tRoot, "privacy.exportFailed", ERROR_OPTIONS));
    } finally {
      setLoadingExport(false);
    }
  }

  function handleDeleteRequest(e: React.FormEvent) {
    e.preventDefault();
    setDeleteError(null);
    // LRN-15: a deliberate second step before anything is scheduled.
    setConfirming(true);
  }

  async function confirmDeletion() {
    setLoadingDelete(true);
    setDeleteError(null);
    try {
      const res = await apiFetch("/v1/privacy/delete-request", {
        method: "POST",
        body: JSON.stringify({ confirmEmail }),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
    } catch (err) {
      setDeleteError(apiErrorText(err, tRoot, "privacy.deleteFailed", ERROR_OPTIONS));
      setLoadingDelete(false);
      setConfirming(false);
      return;
    }

    // LRN-15: the session is already unusable once deletion is pending, so logging out may fail —
    // the client must still sign out locally, whatever the server says.
    try {
      await logoutSession();
    } catch {
      // ignored: see above
    } finally {
      signOutOnLeave.current = true;
      navigate(
        `/t/${tenantSlug}/account/restore?scheduled=1&email=${encodeURIComponent(userEmail)}`,
      );
    }
  }

  return (
    <div className="space-y-10">
      {schoolTenant?.aiTutorEnabled === true ? (
        <section
          className="rounded-lg border border-[var(--border)] p-6"
          data-testid="privacy-ai-disclosure"
        >
          <h2 className="text-lg font-semibold">{t("aiDisclosureTitle")}</h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-[var(--text-muted)]">
            <li data-testid="privacy-ai-sent">{t("aiSent")}</li>
            <li data-testid="privacy-ai-never-sent">{t("aiNeverSent")}</li>
            <li data-testid="privacy-ai-retention">{t("aiRetention")}</li>
          </ul>
        </section>
      ) : null}

      <section className="rounded-lg border border-[var(--border)] p-6">
        <h2 className="text-lg font-semibold">{t("exportTitle")}</h2>
        <p className="mt-2 text-sm text-[var(--text-muted)]">{t("exportDescription")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="privacy-export-json"
            disabled={loadingExport}
            onClick={() => void handleExport("json")}
            className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
          >
            {t("downloadJson")}
          </button>
          <button
            type="button"
            data-testid="privacy-export-csv"
            disabled={loadingExport}
            onClick={() => void handleExport("csv")}
            className="rounded-md border border-[var(--border-strong)] px-4 py-2 text-sm hover:bg-[var(--surface-3)]"
          >
            {t("downloadCsv")}
          </button>
        </div>
        {exportMessage && (
          <p
            role="status"
            className="mt-3 text-sm text-green-700"
            data-testid="privacy-export-message"
          >
            {exportMessage}
          </p>
        )}
        {exportError && (
          <p
            role="alert"
            className="mt-3 text-sm text-[var(--danger)]"
            data-testid="privacy-export-error"
          >
            {exportError}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-red-200 p-6 dark:border-red-900/40">
        <h2 className="text-lg font-semibold text-red-800 dark:text-red-300">{t("deleteTitle")}</h2>
        <p className="mt-2 text-sm text-[var(--text-muted)]">
          {t("deleteDescription", { school })}
        </p>
        <form
          onSubmit={handleDeleteRequest}
          className="mt-4 space-y-3"
          data-testid="privacy-delete-form"
        >
          <div>
            <label htmlFor="confirmEmail" className="mb-1 block text-sm font-medium">
              {t("confirmEmail", { email: userEmail })}
            </label>
            <input
              id="confirmEmail"
              data-testid="privacy-delete-email"
              type="email"
              required
              value={confirmEmail}
              onChange={(e) => {
                setConfirmEmail(e.target.value);
                setConfirming(false);
              }}
              className="w-full max-w-md rounded-md border border-[var(--border-strong)] px-3 py-2"
            />
          </div>
          {deleteError && (
            <p role="alert" className="text-sm text-red-600" data-testid="privacy-delete-error">
              {deleteError}
            </p>
          )}
          {confirming ? (
            <div
              role="alertdialog"
              aria-labelledby="privacy-delete-confirm-text"
              className="space-y-3 rounded-md border border-red-300 p-4"
              data-testid="privacy-delete-confirm"
            >
              <p id="privacy-delete-confirm-text" className="text-sm">
                {t("deleteConfirmText", { school })}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="privacy-delete-confirm-yes"
                  disabled={loadingDelete}
                  onClick={() => void confirmDeletion()}
                  className="rounded-md bg-red-700 px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                  {loadingDelete ? t("submitting") : t("deleteConfirmYes")}
                </button>
                <button
                  type="button"
                  ref={confirmNoButton}
                  data-testid="privacy-delete-confirm-no"
                  disabled={loadingDelete}
                  onClick={() => setConfirming(false)}
                  className="rounded-md border border-[var(--border-strong)] px-4 py-2 text-sm"
                >
                  {t("deleteConfirmNo")}
                </button>
              </div>
            </div>
          ) : (
            <button
              ref={deleteSubmitButton}
              type="submit"
              data-testid="privacy-delete-submit"
              className="rounded-md border border-red-300 px-4 py-2 text-sm text-red-800 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/30"
            >
              {t("requestDeletion")}
            </button>
          )}
        </form>
      </section>

      <p className="text-sm">
        <Link to={`/t/${tenantSlug}/dashboard`} className="text-[var(--text-heading)] underline">
          {t("backToDashboard")}
        </Link>
      </p>
    </div>
  );
}
